import { PAYROLL_DEDUCTION_TYPES, PAYROLL_EXTRA_TYPES } from '@felix/domain';
import { Badge, Button, Group, Modal, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { errorCode } from '../personnel/source.js';
import classes from './Payroll.module.css';
import type { DeductionTypeEditor, ExtraTypeEditor } from './source.js';
import type { PayrollDeductionTypeItem, PayrollExtraTypeItem } from './types.js';

export interface PayrollDialogProps {
  title: string;
  text: string;
  /** Gerekçe alanı ve sınırı; yoksa alan çizilmez. */
  reason?: { hint: string; max: number } | undefined;
  /**
   * Onay adımı (dönemi kapama ve yeniden açma, ADR 0061 §3): parola alanı yalnız şirket ayarı
   * açıkken ya da sunucu isterse görünür (ADR 0075); kapalıyken bilgi metni.
   */
  password?: boolean;
  confirmLabel: string;
  onClose: () => void;
  /** `password` yalnız girildiyse gelir. */
  onConfirm: (input: { reason: string; password?: string }) => Promise<void>;
}

/** Hata kodunun metni (`payroll.errors.codes`); bilinmiyorsa genel metin ve kod. */
export function usePayrollError() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`payroll.errors.codes.${code}`, { defaultValue: '' }) || t('payroll.errors.save', { code });
}

/**
 * Bordronun onay penceresi (ADR 0061 §3, §4): kaldırma (gerekçe), mutabakat, dönemi kapama
 * (onay adımı) ve yeniden açma (gerekçe ve onay adımı).
 */
export function PayrollDialog(props: PayrollDialogProps) {
  const { t } = useTranslation();
  const message = usePayrollError();
  const [reason, setReason] = useState('');
  const reauth = useReauth('payroll_period');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const missing =
    (props.reason !== undefined && reason.trim().length < 5) ||
    (props.password === true && reauth.missing);
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={props.title}
      centered
      closeButtonProps={{ 'aria-label': t('payroll.actions.closeWindow') }}
    >
      <form
        className={classes.panelForm}
        onSubmit={async (event) => {
          event.preventDefault();
          if (missing || busy) return;
          setBusy(true);
          setError(null);
          try {
            await props.onConfirm({
              reason: reason.trim(),
              ...(props.password ? reauth.body() : {}),
            });
          } catch (failure) {
            reauth.noteError(failure);
            setError(message(errorCode(failure)));
            setBusy(false);
          }
        }}
      >
        <p className={classes.note}>{props.text}</p>
        {props.reason ? (
          <Textarea
            label={t('payroll.dialogs.reason')}
            description={props.reason.hint}
            required
            autosize
            minRows={2}
            maxLength={props.reason.max}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        ) : null}
        {props.password ? (
          <ReauthField reauth={reauth} label={t('payroll.dialogs.password')} mb="0" />
        ) : null}
        {error ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={props.onClose}>
            {t('payroll.actions.cancel')}
          </Button>
          <Button type="submit" disabled={missing} loading={busy}>
            {props.confirmLabel}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

/**
 * Şirketin extra türleri (ADR 0068 §2): ofiste payroll yetkilisi ekler (`create`), yeniden
 * adlandırır (`edit`) ve gerekçeyle kaldırır (`void`); hazır türler yerleşiktir ve listelenir.
 */
export function ExtraTypesDialog(props: {
  types: readonly PayrollExtraTypeItem[];
  editor: ExtraTypeEditor;
  can: { create: boolean; edit: boolean; void: boolean };
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const message = usePayrollError();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<PayrollExtraTypeItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await props.onChanged();
      return true;
    } catch (failure) {
      setError(message(errorCode(failure)));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (removing) {
    return (
      <PayrollDialog
        title={t('payroll.dialogs.removeTitle')}
        text={`${removing.name} — ${t('payroll.dialogs.removeText')}`}
        reason={{ hint: t('payroll.dialogs.reasonHint'), max: 500 }}
        confirmLabel={t('payroll.actions.remove')}
        onClose={() => setRemoving(null)}
        onConfirm={async ({ reason }) => {
          await props.editor.remove(removing.id, removing.rowVersion, reason);
          await props.onChanged();
          setRemoving(null);
        }}
      />
    );
  }
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={t('payroll.dialogs.extraTypesTitle')}
      centered
      size="lg"
      closeButtonProps={{ 'aria-label': t('payroll.actions.closeWindow') }}
    >
      <div className={classes.panelForm}>
        <p className={classes.note}>{t('payroll.dialogs.extraTypesText')}</p>
        <section className={classes.extras} aria-label={t('payroll.dialogs.companyTypes')}>
          <span className={classes.fieldLabel}>{t('payroll.dialogs.companyTypes')}</span>
          {props.types.length === 0 ? (
            <p className={classes.sub}>{t('payroll.dialogs.noCompanyTypes')}</p>
          ) : (
            <ul className={classes.extraList}>
              {props.types.map((type) =>
                editing?.id === type.id ? (
                  <li key={type.id}>
                    <TextInput
                      aria-label={t('payroll.dialogs.typeName')}
                      maxLength={100}
                      value={editing.name}
                      onChange={(e) => setEditing({ id: type.id, name: e.currentTarget.value })}
                    />
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="compact-sm"
                        loading={busy}
                        disabled={editing.name.trim() === ''}
                        onClick={async () => {
                          const ok = await run(() =>
                            props.editor.save({
                              id: type.id,
                              rowVersion: type.rowVersion,
                              name: editing.name,
                            }),
                          );
                          if (ok) setEditing(null);
                        }}
                      >
                        {t('payroll.actions.update')}
                      </Button>
                      <Button size="compact-sm" variant="default" onClick={() => setEditing(null)}>
                        {t('payroll.actions.cancel')}
                      </Button>
                    </Group>
                  </li>
                ) : (
                  <li key={type.id}>
                    <span>{type.name}</span>
                    <Group gap="xs" wrap="nowrap">
                      {props.can.edit ? (
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={() => setEditing({ id: type.id, name: type.name })}
                        >
                          {t('payroll.actions.rename')}
                        </Button>
                      ) : null}
                      {props.can.void ? (
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          color="red"
                          onClick={() => setRemoving(type)}
                        >
                          {t('payroll.actions.remove')}
                        </Button>
                      ) : null}
                    </Group>
                  </li>
                ),
              )}
            </ul>
          )}
        </section>
        {props.can.create ? (
          <form
            className={classes.formRow}
            onSubmit={async (event) => {
              event.preventDefault();
              if (name.trim() === '' || busy) return;
              if (await run(() => props.editor.save({ name }))) setName('');
            }}
          >
            <TextInput
              label={t('payroll.dialogs.typeName')}
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <Group align="flex-end">
              <Button type="submit" loading={busy} disabled={name.trim() === ''}>
                {t('payroll.actions.addType')}
              </Button>
            </Group>
          </form>
        ) : null}
        {error ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <section className={classes.extras} aria-label={t('payroll.dialogs.builtIn')}>
          <span className={classes.fieldLabel}>{t('payroll.dialogs.builtIn')}</span>
          <p className={classes.sub}>
            {PAYROLL_EXTRA_TYPES.map((code) => t(`payroll.extraTypes.${code}`)).join(' · ')}
          </p>
        </section>
        <Group justify="flex-end">
          <Button variant="default" onClick={props.onClose}>
            {t('payroll.actions.cancel')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/**
 * Şirketin kesinti türleri (ADR 0080 §2): ofiste payroll yetkilisi ekler (`create`; ad İngilizce,
 * isteğe bağlı Türkçe), yeniden adlandırır ve pasifler/etkinleştirir (`edit`). Tür silinmez:
 * pasif tür yeni kesintide seçilmez, eski kayıtta adını korur. Hazır türler yerleşiktir.
 */
export function DeductionTypesDialog(props: {
  types: readonly PayrollDeductionTypeItem[];
  editor: DeductionTypeEditor;
  can: { create: boolean; edit: boolean };
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const message = usePayrollError();
  const [name, setName] = useState('');
  const [nameTr, setNameTr] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string; nameTr: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await props.onChanged();
      return true;
    } catch (failure) {
      setError(message(errorCode(failure)));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = (type: PayrollDeductionTypeItem, change: Partial<PayrollDeductionTypeItem>) =>
    run(() =>
      props.editor.save({
        id: type.id,
        rowVersion: type.rowVersion,
        name: change.name ?? type.name,
        nameTr: change.nameTr !== undefined ? change.nameTr : type.nameTr,
        active: change.active ?? type.active,
      }),
    );
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={t('payroll.dialogs.deductionTypesTitle')}
      centered
      size="lg"
      closeButtonProps={{ 'aria-label': t('payroll.actions.closeWindow') }}
    >
      <div className={classes.panelForm}>
        <p className={classes.note}>{t('payroll.dialogs.deductionTypesText')}</p>
        <section className={classes.extras} aria-label={t('payroll.dialogs.companyTypes')}>
          <span className={classes.fieldLabel}>{t('payroll.dialogs.companyTypes')}</span>
          {props.types.length === 0 ? (
            <p className={classes.sub}>{t('payroll.dialogs.noCompanyTypes')}</p>
          ) : (
            <ul className={classes.extraList}>
              {props.types.map((type) =>
                editing?.id === type.id ? (
                  <li key={type.id}>
                    <Group gap="xs" wrap="wrap">
                      <TextInput
                        aria-label={t('payroll.dialogs.typeNameEn')}
                        maxLength={100}
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })}
                      />
                      <TextInput
                        aria-label={t('payroll.dialogs.typeNameTr')}
                        maxLength={100}
                        value={editing.nameTr}
                        onChange={(e) => setEditing({ ...editing, nameTr: e.currentTarget.value })}
                      />
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="compact-sm"
                        loading={busy}
                        disabled={editing.name.trim() === ''}
                        onClick={async () => {
                          const ok = await save(type, {
                            name: editing.name,
                            nameTr: editing.nameTr.trim() === '' ? null : editing.nameTr,
                          });
                          if (ok) setEditing(null);
                        }}
                      >
                        {t('payroll.actions.update')}
                      </Button>
                      <Button size="compact-sm" variant="default" onClick={() => setEditing(null)}>
                        {t('payroll.actions.cancel')}
                      </Button>
                    </Group>
                  </li>
                ) : (
                  <li key={type.id}>
                    <span>
                      {type.name}
                      {type.nameTr ? <span className={classes.sub}> · {type.nameTr}</span> : null}
                      {type.active ? null : (
                        <Badge ml="xs" size="sm" color="gray" variant="light">
                          {t('payroll.dialogs.inactive')}
                        </Badge>
                      )}
                    </span>
                    {props.can.edit ? (
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={() =>
                            setEditing({ id: type.id, name: type.name, nameTr: type.nameTr ?? '' })
                          }
                        >
                          {t('payroll.actions.rename')}
                        </Button>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          {...(type.active ? { color: 'red' } : {})}
                          loading={busy}
                          onClick={() => void save(type, { active: !type.active })}
                        >
                          {type.active
                            ? t('payroll.actions.deactivate')
                            : t('payroll.actions.activate')}
                        </Button>
                      </Group>
                    ) : null}
                  </li>
                ),
              )}
            </ul>
          )}
        </section>
        {props.can.create ? (
          <form
            className={classes.formRow}
            onSubmit={async (event) => {
              event.preventDefault();
              if (name.trim() === '' || busy) return;
              const ok = await run(() =>
                props.editor.save({
                  name,
                  nameTr: nameTr.trim() === '' ? null : nameTr,
                  active: true,
                }),
              );
              if (ok) {
                setName('');
                setNameTr('');
              }
            }}
          >
            <TextInput
              label={t('payroll.dialogs.typeNameEn')}
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label={t('payroll.dialogs.typeNameTr')}
              maxLength={100}
              value={nameTr}
              onChange={(e) => setNameTr(e.currentTarget.value)}
            />
            <Group align="flex-end">
              <Button type="submit" loading={busy} disabled={name.trim() === ''}>
                {t('payroll.actions.addType')}
              </Button>
            </Group>
          </form>
        ) : null}
        {error ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <section className={classes.extras} aria-label={t('payroll.dialogs.builtIn')}>
          <span className={classes.fieldLabel}>{t('payroll.dialogs.builtIn')}</span>
          <p className={classes.sub}>
            {PAYROLL_DEDUCTION_TYPES.map((code) => t(`payroll.kinds.${code}`)).join(' · ')}
          </p>
        </section>
        <Group justify="flex-end">
          <Button variant="default" onClick={props.onClose}>
            {t('payroll.actions.cancel')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}
